import { Phone, Mail, Globe } from "lucide-react";
import { formatUSD } from "../utils/currency";

export default function InvoicePreview({ invoice, clientData, providerInfo, logoSrc }) {
  const items = invoice.items || [];
  const total = items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
  const isCommercial = !!clientData.company_name;
  const showPaidStamp = !!invoice.paid_in_advance || invoice.status === "paid";
  // When rendered inside html2canvas (public invoice PDF export), the caller
  // pre-fetches the logo and passes it as a Base64 data URI to guarantee the
  // canvas is never tainted by cross-origin/CDN quirks in production.
  const logo = logoSrc || "/brand/gow-invoice-logo.png";

  return (
    <div id="invoice-preview" className="relative bg-white font-sans text-[12.5px] leading-relaxed shadow-lg rounded-lg overflow-hidden" style={{ width: "100%", minHeight: 700 }}>
      {/* PAID stamp — rendered on top of all content at 35% opacity */}
      {showPaidStamp && (
        <div
          data-testid="paid-stamp"
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(-18deg)",
            pointerEvents: "none",
            zIndex: 50,
            opacity: 0.55,
          }}
        >
          <div
            style={{
              border: "6px solid #16a34a",
              color: "#16a34a",
              padding: "12px 42px",
              borderRadius: "8px",
              fontSize: "96px",
              fontWeight: 900,
              letterSpacing: "12px",
              fontFamily: "Arial, sans-serif",
              textShadow: "2px 2px 0 rgba(22,163,74,0.2)",
              whiteSpace: "nowrap",
              boxShadow: "inset 0 0 0 3px rgba(22,163,74,0.25)",
            }}
          >
            PAID
          </div>
        </div>
      )}
      <div className="h-2 bg-gradient-to-r from-teal-600 to-slate-700" />
      <div className="px-8 pt-6 pb-4 flex justify-between items-start">
        <div className="flex items-center gap-3 mb-1">
          <img
            src={logo}
            alt="Georgia Outdoor Worx"
            style={{ height: 100, width: "auto" }}
          />
        </div>
        <div className="text-right">
          <h1 className="text-3xl font-bold text-teal-700 tracking-wider">INVOICE</h1>
          <div className="mt-2 text-xs">
            <div className="flex justify-end gap-2"><span className="text-stone-500 font-medium">INVOICE NO:</span><span className="font-bold text-stone-800" data-testid="invoice-number">{invoice.invoice_number || "—"}</span></div>
            <div className="flex justify-end gap-2 mt-1"><span className="text-stone-500 font-medium">DATE:</span><span className="font-bold text-stone-800">{invoice.date || new Date().toLocaleDateString("en-US")}</span></div>
            {invoice.due_date && <div className="flex justify-end gap-2 mt-1"><span className="text-stone-500 font-medium">DUE:</span><span className="font-bold text-stone-800">{invoice.due_date}</span></div>}
          </div>
        </div>
      </div>
      <div className="mx-8 h-[3px] bg-teal-600" />
      <div className="px-8 py-5 grid grid-cols-2 gap-6">
        <div>
          <p className="text-[10px] font-bold text-teal-600 uppercase tracking-widest mb-2">BILL TO</p>
          {isCommercial ? (
            <>
              <p className="font-bold text-stone-800">{clientData.name}</p>
              {clientData.tax_id && <p className="text-stone-500 text-xs">EIN: {clientData.tax_id}</p>}
              {clientData.responsible_contact && <p className="text-stone-600 text-xs mt-1">Attn: {clientData.responsible_contact}</p>}
            </>
          ) : (
            <p className="font-bold text-stone-800">{clientData.name || "Client Name"}</p>
          )}
          <p className="text-stone-500 text-xs mt-1">{clientData.address || ""}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold text-teal-600 uppercase tracking-widest mb-2">FROM</p>
          <p className="font-bold text-stone-800">{providerInfo.name}</p>
          <div className="mt-2 text-xs text-stone-500 space-y-0.5">
            <p><span className="font-medium text-stone-600">EIN:</span> 39-3769032</p>
            <p><span className="font-medium text-stone-600">Currency:</span> {providerInfo.currency}</p>
            {invoice.show_bank_details && (
              <div data-testid="bank-details-block" className="mt-2 pt-2 border-t border-stone-200 space-y-0.5">
                <p className="font-semibold text-stone-700 text-[11px] uppercase tracking-wider">Payment Details</p>
                <p><span className="font-medium text-stone-600">Bank:</span> Truist Bank</p>
                <p><span className="font-medium text-stone-600">Account:</span> 1110027939728</p>
                <p><span className="font-medium text-stone-600">Routing:</span> 061113415</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="px-8 pb-4">
        <table className="w-full border-collapse">
          <thead><tr className="bg-teal-700 text-white text-[11px] uppercase tracking-wider">
            <th className="text-left px-3 py-2.5 font-semibold rounded-tl-lg">Service Description</th>
            <th className="text-right px-3 py-2.5 font-semibold w-28">Unit Price</th>
            <th className="text-center px-3 py-2.5 font-semibold w-16">Qty</th>
            <th className="text-right px-3 py-2.5 font-semibold w-28 rounded-tr-lg">Subtotal</th>
          </tr></thead>
          <tbody>
            {items.filter(i => i.description).length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-stone-300 italic">Add services...</td></tr>
            ) : items.filter(i => i.description).map((item, idx) => (
              <tr key={`inv-item-${idx}`} className={`${idx % 2 === 0 ? "bg-stone-50" : "bg-white"} border-b border-stone-100`}>
                <td className="px-3 py-2.5 text-stone-700">{item.description}</td>
                <td className="px-3 py-2.5 text-right text-stone-700">{formatUSD(item.unit_price || 0)}</td>
                <td className="px-3 py-2.5 text-center text-stone-700">{item.quantity || 0}</td>
                <td className="px-3 py-2.5 text-right font-medium text-stone-800">{formatUSD((item.quantity || 0) * (item.unit_price || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end mt-1"><div className="bg-teal-700 text-white px-6 py-3 rounded-b-lg flex items-center gap-6">
          <span className="text-sm font-bold tracking-wider">TOTAL</span><span className="text-xl font-bold">{formatUSD(total)}</span>
        </div></div>
      </div>
      {invoice.notes && <div className="px-8 py-3"><p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Notes</p><p className="text-stone-600 text-xs whitespace-pre-line">{invoice.notes}</p></div>}
      <div className="mt-auto"><div className="mx-8 h-[2px] bg-teal-600" />
        <div className="px-8 py-4 flex items-center justify-between">
          <div><p className="text-xl font-bold text-teal-700 mb-1">THANK YOU!</p>
            <div className="flex items-center gap-4 text-xs text-stone-500">
              <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-teal-600" /> {providerInfo.phone}</span>
              <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-teal-600" /> {providerInfo.email}</span>
              <span className="flex items-center gap-1"><Globe className="w-3 h-3 text-teal-600" /> {providerInfo.website}</span>
            </div>
          </div>
          <div className="flex items-center gap-2"><img src={logo} alt="GOW" style={{ height: 42, width: "auto" }} /><span className="text-xs font-bold text-stone-600">Georgia Outdoor Worx</span></div>
        </div>
      </div>
    </div>
  );
}
